import { getScheduleFeasibilityIcons } from 'lib/utils'
import React from 'react'
import Tooltip from '@mui/material/Tooltip'
import { isEqual, isBefore, isAfter } from 'date-fns'

const getColorCode = ({ initialCompletionDate, currentCompletionDate, debtMaturityDate }) => {
  if (
    (isBefore(currentCompletionDate, debtMaturityDate) &&
      isBefore(currentCompletionDate, initialCompletionDate)) ||
    (isEqual(currentCompletionDate, initialCompletionDate) &&
      isBefore(currentCompletionDate, debtMaturityDate))
  ) {
    return { barColor: '#8fbd90', iconColor: '#697c4f' }
  } else if (
    isBefore(currentCompletionDate, debtMaturityDate) &&
    isAfter(currentCompletionDate, initialCompletionDate)
  ) {
    return { barColor: '#f3da76', iconColor: '#f3da76' }
  } else if (isAfter(currentCompletionDate, debtMaturityDate)) {
    return { barColor: '#e9a17d', iconColor: '#efa57f' }
  } else {
    return { barColor: '#e9a17d', iconColor: '#efa57f' }
  }
}

export default function ScheduleFeasibility({ data }) {
  const items = data?.map((item) => ({
    project: item.project,
    initialCompletionDate: new Date(item.initial_completion_date).toDateString(),
    currentCompletionDate: new Date(item.current_completion_date).toDateString(),
    debtMaturityDate: new Date(item.debt_maturity_date).toDateString(),
    gap: item.feasibility_gap,
  }))

  return (
    <div className="space-y-4 flex flex-col xl:-ml-5 2xl:-ml-0 justify-center">
      {items?.map((item) => (
        <div
          className="grid grid-cols-[20px_auto] w-[200px] 2xl:w-[230px] items-end "
          key={item.value}
        >
          <div
            className="  w-5 h-5  rounded-full -mb-1.5 container-shadow"
            style={{
              backgroundColor: getColorCode(item).barColor,
            }}
          >
            {item.value}
          </div>
          <div>
            <div className="flex justify-evenly">
              {getScheduleFeasibilityIcons(item).map((icon) => (
                <>
                  <Tooltip
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -14],
                            },
                          },
                        ],
                      },
                    }}
                    title={
                      icon === 'calendar' ? (
                        <div className="flex flex-col gap-y-2 text-sm">
                          <span>Current Completion Date</span>
                          <span>{item.currentCompletionDate}</span>
                        </div>
                      ) : icon === 'target' ? (
                        <div className="flex flex-col gap-y-2 text-sm">
                          <span> Initial Completion Date</span>
                          <span>{item.initialCompletionDate}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-y-2 text-sm">
                          <span>Debt Maturity Date</span>
                          <span>{item.debtMaturityDate}</span>
                        </div>
                      )
                    }
                  >
                    {icon === 'calendar' && <CalendarIcon item={item} />}
                    {icon === 'target' && <TargetIcon item={item} />}
                    {icon === 'dollar' && <DollarIcon item={item} />}
                  </Tooltip>
                </>
              ))}
            </div>
            <Tooltip
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -14],
                      },
                    },
                  ],
                },
              }}
              title={
                <div className="flex flex-col gap-y-2 text-sm">
                  <span>{item.project} </span>
                  <span>Feasibility gap: {item.gap} </span>
                </div>
              }
            >
              <div
                className="relative -ml-0.5  w-[180px] 2xl:w-[230px] container-shadow h-2 rounded-br-full rounded-tr-full"
                style={{
                  backgroundColor: getColorCode(item).barColor,
                }}
              ></div>
            </Tooltip>
          </div>
        </div>
      ))}
    </div>
  )
}

const CalendarIcon = ({ item }) => {
  const fillColor = getColorCode(item).iconColor
  const barColor = getColorCode(item).barColor

  return (
    <div className="flex flex-col items-center ">
      <svg width="25" xmlns="http://www.w3.org/2000/svg" height="25" fill="none">
        <g style={{ fill: '#000000' }}>
          <g clip-path="url(#a)" style={{ fill: fillColor }}>
            <defs>
              <clipPath id="a" class="svg-def" transform="scale(.06667)">
                <path d="M17.387 17.387h340.5v340.5h-340.5Zm0 0" />
              </clipPath>
            </defs>
            <path
              d="M20.832 11.391V3.429a.758.758 0 0 0-.756-.757h-2.27v-.378a1.136 1.136 0 0 0-2.27 0v.378h-3.405v-.378a1.136 1.136 0 0 0-2.27 0v.378H6.456v-.378a1.136 1.136 0 0 0-2.27 0v.378h-2.27a.758.758 0 0 0-.757.757v17.403c0 .418.339.757.757.757h10.072a6.783 6.783 0 0 0 5.061 2.27 6.818 6.818 0 0 0 6.81-6.81 6.806 6.806 0 0 0-3.027-5.658Zm-4.54-9.097a.379.379 0 0 1 .757 0v1.513a.38.38 0 0 1-.757 0Zm-5.675 0a.379.379 0 0 1 .757 0v1.513a.38.38 0 0 1-.757 0Zm-5.675 0a.379.379 0 0 1 .757 0v1.513a.38.38 0 0 1-.757 0Zm-.756 1.135v.378a1.136 1.136 0 0 0 2.27 0v-.378h3.405v.378a1.136 1.136 0 0 0 2.27 0v-.378h3.405v.378a1.136 1.136 0 0 0 2.27 0v-.378h2.27v2.27H1.916v-2.27Zm-2.27 17.403V6.456h18.16v4.502a6.757 6.757 0 0 0-3.027-.719 6.818 6.818 0 0 0-6.81 6.81c0 1.399.425 2.7 1.152 3.783Zm15.133 2.27a6.06 6.06 0 0 1-6.053-6.053 6.06 6.06 0 0 1 6.053-6.053 6.06 6.06 0 0 1 6.053 6.053 6.06 6.06 0 0 1-6.053 6.053Zm0 0"
              style={{ fill: fillColor, fillOpacity: 1 }}
              class="fills"
            />
          </g>
          <path
            d="m20.465 13.993-.711-.457a.802.802 0 0 0-1.105.242l-2.835 4.41-.722-.577a.803.803 0 0 0-1.125.125l-.526.658a.798.798 0 0 0 .125 1.126l1.779 1.422a1.219 1.219 0 0 0 1.791-.292l3.569-5.551a.8.8 0 0 0-.24-1.106Zm-.396.697-3.568 5.55a.468.468 0 0 1-.683.112l-1.78-1.424c-.016-.012-.02-.045-.006-.061l.526-.658a.045.045 0 0 1 .034-.017c.01 0 .02.003.027.01l1.049.839a.376.376 0 0 0 .298.078.376.376 0 0 0 .257-.168l3.063-4.765a.044.044 0 0 1 .059-.013l.724.517Zc.003-.049.01-.017 0 0Zm0 0"
            style={{ fill: fillColor, fillOpacity: 1 }}
            class="fills"
          />
        </g>
      </svg>
      <svg width="2" xmlns="http://www.w3.org/2000/svg" height="10" fill="none">
        <rect
          rx="1"
          ry="1"
          width="2"
          height="10"
          style={{ fill: barColor, fillOpacity: 1 }}
          class="fills"
        />
      </svg>
    </div>
  )
}

const TargetIcon = ({ item }) => {
  const fillColor = getColorCode(item).iconColor
  const barColor = getColorCode(item).barColor

  return (
    <div className="flex flex-col items-center ">
      <svg width="25" xmlns="http://www.w3.org/2000/svg" height="25" fill="none">
        <g style={{ fill: '#000000' }}>
          <g clip-path="url(#a)" style={{ fill: '#000000' }}>
            <defs>
              <clipPath id="a" class="svg-def" transform="scale(.06667)">
                <path d="M22.129 23.367H351v328.5H22.129Zm0 0" />
              </clipPath>
            </defs>
            <path
              d="M21.297 7.324a10.072 10.072 0 0 1 1.419 5.184c0 5.663-4.599 10.265-10.258 10.265-5.66 0-10.259-4.602-10.259-10.265S6.798 2.242 12.458 2.242c2.308 0 4.428.77 6.137 2.053.069-.222.171-.427.308-.616a10.823 10.823 0 0 0-6.445-2.121c-6.036 0-10.943 4.91-10.943 10.95 0 6.039 4.907 10.95 10.943 10.95 6.035 0 10.942-4.911 10.942-10.95 0-1.968-.513-3.798-1.42-5.39a1.858 1.858 0 0 1-.683.206Zm0 0"
              style={{ fill: fillColor, fillOpacity: 1 }}
              class="fills"
            />
          </g>
          <path
            d="M23.28 5.082c.205-.188.12-.53-.154-.581l-1.351-.308-.205-1.318c-.034-.256-.376-.376-.564-.205l-1.436 1.3a1.098 1.098 0 0 0-.342.668l-.154 1.557-6.24 5.594.034-.085a.33.33 0 0 0-.205-.428.33.33 0 0 0-.428.205l-.427 1.249a.357.357 0 0 0 .068.342c.086.086.205.137.325.103l1.316-.274a.35.35 0 0 0 .274-.41.35.35 0 0 0-.41-.274l6.155-5.526 1.641-.034a1 1 0 0 0 .65-.257c0-.017 1.453-1.318 1.453-1.318Zm0 0"
            style={{ fill: fillColor, fillOpacity: 1 }}
            class="fills"
          />
          <path
            d="m13.569 10.215 4.838-4.363.034-.359a9.12 9.12 0 0 0-5.983-2.224c-5.095 0-9.233 4.14-9.233 9.239 0 5.099 4.138 9.239 9.233 9.239 5.094 0 9.232-4.14 9.232-9.239a9.2 9.2 0 0 0-1.573-5.15h-.325l-4.89 4.414h.018c.068.24.102.479.102.736a2.561 2.561 0 0 1-2.564 2.566 2.562 2.562 0 0 1-2.565-2.566 2.562 2.562 0 0 1 2.565-2.567c.41 0 .786.103 1.111.274Zm5.385 1.951h1.026c.188 0 .342.154.342.342a.343.343 0 0 1-.342.342h-1.026a.342.342 0 0 1 0-.684Zm-1.897 4.944a.33.33 0 0 1 .478 0l.479.479a.33.33 0 0 1 0 .479c-.068.069-.256.189-.479 0l-.478-.479c-.137-.136-.137-.359 0-.479Zm-4.942 1.899a.342.342 0 0 1 .684 0v1.027a.342.342 0 0 1-.684 0ZM5.96 12.85H4.935a.343.343 0 0 1-.342-.342c0-.188.154-.342.342-.342H5.96a.342.342 0 0 1 0 .684Zm1.898 4.739-.478.479c-.137.137-.359.137-.479 0a.33.33 0 0 1 0-.479l.479-.479a.33.33 0 0 1 .478 0c.137.12.137.343 0 .479Zm0-9.684c-.136.137-.359.137-.478 0l-.479-.479a.33.33 0 0 1 0-.479.33.33 0 0 1 .479 0l.478.479a.33.33 0 0 1 0 .479Zm4.941-1.899a.342.342 0 1 1-.684 0V4.98a.342.342 0 0 1 .684 0Zm0 0"
            style={{ fill: fillColor, fillOpacity: 1 }}
            class="fills"
          />
          <path
            d="M11.568 11.276c.103-.291.325-.53.616-.633h-.017a1.893 1.893 0 0 0-1.59 1.865 1.876 1.876 0 0 0 3.573.804.94.94 0 0 1-.513.274l-1.316.273c-.069.018-.137.018-.206.018-.29 0-.581-.12-.769-.343a1.035 1.035 0 0 1-.205-1.009Zm0 0"
            style={{ fill: fillColor, fillOpacity: 1 }}
            class="fills"
          />
        </g>
      </svg>
      <svg width="2" xmlns="http://www.w3.org/2000/svg" height="10" fill="none">
        <rect
          rx="1"
          ry="1"
          width="2"
          height="10"
          style={{ fill: barColor, fillOpacity: 1 }}
          class="fills"
        />
      </svg>
    </div>
  )
}

const DollarIcon = ({ item }) => {
  const fillColor = getColorCode(item).iconColor
  const barColor = getColorCode(item).barColor

  return (
    <div className="flex flex-col items-center ">
      <svg width="25" xmlns="http://www.w3.org/2000/svg" height="25" fill="none">
        <g style={{ fill: '#000000' }}>
          <g clip-path="url(#a)" style={{ fill: '#000000' }}>
            <defs>
              <clipPath id="a" class="svg-def" transform="scale(.06667)">
                <path d="M25 26h333.063v326H25Zm0 0" />
              </clipPath>
            </defs>
            <path
              d="M10.623 9.799c.026 0 .052.003 0 0Za.54.54 0 0 1 .153.031.416.416 0 0 1 .217.217.405.405 0 0 1 .03.154v.784h.007a1.753 1.753 0 0 1 .354.036 1.99 1.99 0 0 1 .34.104 1.83 1.83 0 0 1 1.123 1.679.497.497 0 0 1-.027.158.408.408 0 0 1-.376.257.434.434 0 0 1-.157-.032.431.431 0 0 1-.182-.153.484.484 0 0 1-.058-.15.425.425 0 0 1-.005-.08 1.006 1.006 0 0 0-.078-.387 1.078 1.078 0 0 0-.22-.328 1.018 1.018 0 0 0-.714-.298h-.815a1.005 1.005 0 0 0-.562.171 1.025 1.025 0 0 0-.43.645 1.078 1.078 0 0 0 0 .395 1.067 1.067 0 0 0 .151.365 1.025 1.025 0 0 0 .644.43c.065.013.131.02.197.02h.815c.059 0 .118.004.177.011a1.652 1.652 0 0 1 .511.134 1.792 1.792 0 0 1 .699.528 1.877 1.877 0 0 1 .27.455 1.944 1.944 0 0 1 .128.514 2.077 2.077 0 0 1 0 .354 2.027 2.027 0 0 1-.069.346 1.831 1.831 0 0 1-.577.874 1.633 1.633 0 0 1-.292.199 1.792 1.792 0 0 1-.67.211 1.502 1.502 0 0 1-.177.011h-.007v.784a.411.411 0 0 1-.03.153.36.36 0 0 1-.087.13.36.36 0 0 1-.061.05.383.383 0 0 1-.3.06.383.383 0 0 1-.145-.06.319.319 0 0 1-.06-.05.36.36 0 0 1-.087-.13.383.383 0 0 1-.031-.153v-.785h-.007a1.786 1.786 0 0 1-.526-.08 1.679 1.679 0 0 1-.329-.137 1.795 1.795 0 0 1-.745-.746 1.822 1.822 0 0 1-.216-.856.432.432 0 0 1 .031-.154.36.36 0 0 1 .087-.131.416.416 0 0 1 .206-.11.415.415 0 0 1 .233.023.383.383 0 0 1 .13.087.36.36 0 0 1 .087.131c.011.024.018.05.023.075a.36.36 0 0 1 .008.079 1.015 1.015 0 0 0 1.011 1.013h.815a1.015 1.015 0 0 0 .935-1.4 1.014 1.014 0 0 0-.935-.625h-.815a2.052 2.052 0 0 1-.351-.039 1.925 1.925 0 0 1-.647-.276 2.012 2.012 0 0 1-.271-.227 1.877 1.877 0 0 1-.312-.426 1.836 1.836 0 0 1-.203-.675 2.054 2.054 0 0 1 0-.353 2.05 2.05 0 0 1 .068-.347 1.951 1.951 0 0 1 .225-.48 1.78 1.78 0 0 1 .352-.394c.046-.038.093-.073.141-.107a1.811 1.811 0 0 1 .998-.314h.007v-.783a.415.415 0 0 1 .068-.223.36.36 0 0 1 .05-.061.362.362 0 0 1 .13-.087.54.54 0 0 1 .153-.031Zc-.052.003-.026 0 0 0Zm12.839.071h-.937l.749.749a.438.438 0 0 1-.225.743.419.419 0 0 1-.254-.025.432.432 0 0 1-.143-.095l-1.098-1.099-.915.917a.517.517 0 0 1-.067.055.499.499 0 0 1-.158.065.412.412 0 0 1-.254-.025.428.428 0 0 1-.197-.162.466.466 0 0 1-.066-.159.417.417 0 0 1 .065-.33.446.446 0 0 1 .055-.067l.566-.566h-.71a.412.412 0 0 1-.169-.034.466.466 0 0 1-.197-.162.437.437 0 0 1-.041-.414.466.466 0 0 1 .162-.197.466.466 0 0 1 .159-.066.412.412 0 0 1 .086-.009h.528l-.566-.566a.488.488 0 0 1-.092-.143.43.43 0 0 1-.031-.166.41.41 0 0 1 .035-.167.45.45 0 0 1 .16-.195.45.45 0 0 1 .241-.075.414.414 0 0 1 .166.031.494.494 0 0 1 .143.092l.711.713a6.273 6.273 0 0 0-.463-2.113 6.406 6.406 0 0 0-.619-1.145 6.702 6.702 0 0 0-.539-.681 6.35 6.35 0 0 0-.973-.863 6.67 6.67 0 0 0-.74-.454 5.638 5.638 0 0 0-.392-.188 6.71 6.71 0 0 0-.391-.138 7.002 7.002 0 0 0-.401-.111 5.744 5.744 0 0 0-1.232-.17 6.384 6.384 0 0 0-.83.025 5.731 5.731 0 0 0-.412.054 6.252 6.252 0 0 0-2.984 1.373h.958a.42.42 0 0 1 .277.102.439.439 0 0 1 .146.258.476.476 0 0 1 .005.1.48.48 0 0 1-.019.099c.02.049.03.101.031.155v.891a8.727 8.727 0 0 1 1.038.294 8.678 8.678 0 0 1 1.473.67 8.291 8.291 0 0 1 .904.589 9.008 9.008 0 0 1 1.564 1.481 9.016 9.016 0 0 1 1.387 2.307 9.026 9.026 0 0 1 .577 2.078 8.706 8.706 0 0 1 .097 1.075 8.806 8.806 0 0 1-.098 1.616 8.583 8.583 0 0 1-.226 1.057 8.767 8.767 0 0 1-.572 1.514 9.457 9.457 0 0 1-.529.942 8.782 8.782 0 0 1-.638.871 9.803 9.803 0 0 1-.737.788 9.008 9.008 0 0 1-3.204 1.951 9.309 9.309 0 0 1-1.569.393 8.78 8.78 0 0 1-1.613.104 8.802 8.802 0 0 1-2.131-.315 8.745 8.745 0 0 1-1.515-.566 9.431 9.431 0 0 1-1.388-.83 8.999 8.999 0 0 1-1.217-1.066 8.595 8.595 0 0 1-.696-.825 8.997 8.997 0 0 1-1.071-1.871 9.041 9.041 0 0 1-.701-3.148 8.84 8.84 0 0 1 .023-1.08 8.91 8.91 0 0 1 .278-1.595 9.032 9.032 0 0 1 1.079-2.465 8.998 8.998 0 0 1 2.183-2.372 8.985 8.985 0 0 1 2.366-1.278 9.03 9.03 0 0 1 .513-.167V4.635a.422.422 0 0 1 .181-.35.4.4 0 0 1 .349-.182h1.32a.427.427 0 0 1 .116-.217 7.007 7.007 0 0 1 3.103-1.848 6.846 6.846 0 0 1 5.448.697c.231.138.453.289.667.454a7.07 7.07 0 0 1 1.525 1.664 7.21 7.21 0 0 1 1.091 2.796c.04.249.067.499.081.751l.692-.692a.45.45 0 0 1 .143-.099.382.382 0 0 1 .083-.026.427.427 0 0 1 .335.064.435.435 0 0 1 .196.371.516.516 0 0 1-.036.17.446.446 0 0 1-.1.143l-.657.658h.754a.461.461 0 0 1 .235.08.422.422 0 0 1 .116.121.473.473 0 0 1 .063.154.469.469 0 0 1-.023.248.431.431 0 0 1-.152.195.411.411 0 0 1-.073.043.633.633 0 0 1-.162.04Zc.056-.006.028-.002 0 0ZM10.673 6.286a8.898 8.898 0 0 0-.8.04 7.583 7.583 0 0 0-.794.118 7.597 7.597 0 0 0-.777.195 8.489 8.489 0 0 0-1.122.433 8.013 8.013 0 0 0-1.373.824 8.607 8.607 0 0 0-.62.51 8.435 8.435 0 0 0-.567.567 8.422 8.422 0 0 0-.954 1.289 8.013 8.013 0 0 0-.811 1.831 8.51 8.51 0 0 0-.195.779 7.577 7.577 0 0 0-.117.794 8.965 8.965 0 0 0-.03.4 8.986 8.986 0 0 0 0 .803 9.01 9.01 0 0 0 .079.799 7.437 7.437 0 0 0 .156.787 8.55 8.55 0 0 0 .233.769 8.044 8.044 0 0 0 1.639 2.738c.09.099.182.196.276.291a7.615 7.615 0 0 0 .911.786 7.413 7.413 0 0 0 .666.446 8.013 8.013 0 0 0 1.448.686 8.489 8.489 0 0 0 1.158.321 8.826 8.826 0 0 0 .794.118 7.602 7.602 0 0 0 .8.039 7.638 7.638 0 0 0 .801-.039 8.826 8.826 0 0 0 1.185-.206 8.489 8.489 0 0 0 1.507-.54 9.267 9.267 0 0 0 .708-.379 8.752 8.752 0 0 0 .666-.446 7.641 7.641 0 0 0 .62-.509c.099-.09.196-.183.291-.277a9.483 9.483 0 0 0 .538-.595c.085-.104.167-.209.246-.317a7.443 7.443 0 0 0 .446-.667 8.047 8.047 0 0 0 .685-1.45 7.419 7.419 0 0 0 .232-.769 7.368 7.368 0 0 0 .157-.787 8.821 8.821 0 0 0 .079-.799 8.941 8.941 0 0 0-.001-.803 8.965 8.965 0 0 0-.079-.798 8.856 8.856 0 0 0-.157-.787 8.52 8.52 0 0 0-.541-1.509 8.015 8.015 0 0 0-1.07-1.692 7.585 7.585 0 0 0-.539-.594 7.585 7.585 0 0 0-.593-.539 9.298 9.298 0 0 0-.316-.248 8.44 8.44 0 0 0-1.373-.824 9.335 9.335 0 0 0-.741-.308 8.048 8.048 0 0 0-1.552-.391 8.821 8.821 0 0 0-.798-.079 8.904 8.904 0 0 0-.401-.011ZM9.045 4.984v.667a8.752 8.752 0 0 1 2.664-.09v-.577Zm0 0"
              style={{ fill: fillColor, fillOpacity: 1 }}
              class="fills"
            />
          </g>
        </g>
      </svg>
      <svg width="2" xmlns="http://www.w3.org/2000/svg" height="10" fill="none">
        <rect
          rx="1"
          ry="1"
          width="2"
          height="10"
          style={{ fill: barColor, fillOpacity: 1 }}
          class="fills"
        />
      </svg>
    </div>
  )
}
